import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  MdArrowBack, MdCalendarToday, MdAccessTime, MdLocalHospital,
  MdPerson, MdContentCopy, MdCancel, MdRefresh,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  bookingId, isEmergencyBooking, isSosBooking, isDoctorBooking,
  fmtTime, fmtDate, statusMeta, shouldShowCheckInOtp,
  displayDoctorName, displayHospitalName,
} from '../utils/bookingHelpers';
import CheckInOtp from '../components/CheckInOtp';

function copyText(text, showToast, msg = 'Copied!') {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(() => showToast(msg, 'success')).catch(() => {});
}

export default function BookingDetailPage() {
  const { bookingId: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const routeState = useLocation().state?.booking;
  const navigate = useNavigate();
  const { user, showToast } = useAuth();

  const [booking, setBooking] = useState(routeState || null);
  const [loading, setLoading] = useState(!routeState);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  const loadBooking = useCallback(async () => {
    if (!user || !routeId) { setLoading(false); return; }
    setLoading(true);

    // Prefer direct fetch — returns full DynamoDB row including `otp`.
    const direct = await api.getBooking(routeId);
    if (direct.data && !direct.error) {
      setBooking(direct.data);
      setLoading(false);
      return;
    }

    const r = await api.getMyBookingsAll(user);
    const items = r.data || [];
    const found = items.find(b => bookingId(b) === routeId);
    if (found) setBooking(found);
    setLoading(false);
  }, [user, routeId]);

  useEffect(() => { loadBooking(); }, [loadBooking]);

  const b = booking;
  const id = bookingId(b);
  const st = (b?.status || 'pending').toLowerCase();
  const sm = statusMeta(st);
  const showOtp = shouldShowCheckInOtp(b, { onDetail: true });
  const isEmerg = isEmergencyBooking(b);
  const isSos = isSosBooking(b);
  const isDoc = isDoctorBooking(b);
  const isCancelled = st === 'cancelled';
  const isCheckedIn = st === 'checked_in';
  const canModify = !isCancelled && !isCheckedIn && !isEmerg;

  const handleBack = () => navigate('/bookings');

  const openSosStatus = () => {
    const params = new URLSearchParams();
    if (b?.alertId) params.set('alertId', b.alertId);
    if (id) params.set('bookingId', id);
    navigate(`/emergency?${params.toString()}`);
  };

  useEffect(() => {
    if (isSos && b?.alertId) openSosStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSos, b?.alertId]);

  const cancelBooking = async () => {
    if (!window.confirm('Cancel this appointment?')) return;
    setCancelling(true);
    const r = await api.cancelBooking(id);
    setCancelling(false);
    if (r.error) { showToast('Failed: ' + r.error, 'error'); return; }
    setBooking(prev => ({ ...prev, status: 'cancelled' }));
    showToast('Appointment cancelled', 'success');
  };

  const openReschedule = async () => {
    if (b?.isRescheduled) { showToast('Already rescheduled once', 'error'); return; }
    setRescheduleOpen(true);
    setSelectedSlot('');
    setLoadingSlots(true);
    const r = await api.getBookedSlots(b.doctorId || 'walkin', b.date, b.hospitalId);
    const booked = r.data?.bookedSlots || [];
    const allSlots = [];
    for (let h = 8; h < 20; h++) {
      for (let m = 0; m < 60; m += 30) {
        const slot = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        if (!booked.includes(slot) && slot !== b.slot) allSlots.push(slot);
      }
    }
    setAvailableSlots(allSlots);
    setLoadingSlots(false);
  };

  const doReschedule = async () => {
    if (!selectedSlot) return;
    setRescheduling(true);
    const r = await api.rescheduleBooking(id, b.date || b.appointmentDate, selectedSlot);
    setRescheduling(false);
    if (r.error) { showToast('Reschedule failed: ' + r.error, 'error'); return; }
    setBooking(prev => ({ ...prev, slot: selectedSlot, isRescheduled: true }));
    setRescheduleOpen(false);
    showToast('Rescheduled to ' + fmtTime(selectedSlot), 'success');
  };

  if (!user) {
    return (
      <div className="page empty">
        <div className="empty-icon">🔐</div>
        <div className="empty-title">Login to view booking</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>Login</button>
      </div>
    );
  }

  if (loading) return <div className="page"><div className="spinner" style={{ margin: '60px auto' }} /></div>;

  if (!b) {
    return (
      <div className="page empty">
        <div className="empty-icon">📭</div>
        <div className="empty-title">Booking not found</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={handleBack}>Back to My Bookings</button>
      </div>
    );
  }

  const forceEmergency = searchParams.get('emergency') === '1' || isEmerg;

  return (
    <div className="fade-in">
      <div className="page" style={{ maxWidth: 520 }}>

        <button type="button" className="btn btn-ghost btn-sm" onClick={handleBack}
          style={{ marginBottom: 12, paddingLeft: 0 }}>
          <MdArrowBack size={18} /> My Bookings
        </button>

        <div className="section-title" style={{ marginBottom: 4 }}>
          {forceEmergency ? '🚨 Emergency Details' : 'Appointment Details'}
        </div>
        <div className="section-sub" style={{ marginBottom: 20 }}>Booking ID: {id}</div>

        {/* Status banner */}
        <div style={{
          padding: '16px 18px', borderRadius: 16, marginBottom: 16,
          background: sm.bg, border: `1px solid ${sm.color}33`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: `${sm.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>
            {forceEmergency ? '🚨' : (isDoc ? '👨‍⚕️' : '🏥')}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: sm.color }}>{sm.label}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {forceEmergency ? 'Emergency OP booking' : (isDoc ? 'Doctor appointment' : 'Hospital OP')}
            </div>
          </div>
        </div>

        {/* Main detail card */}
        <div className="glass" style={{ borderRadius: 20, padding: 20, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>
            {displayDoctorName(b)}
          </div>
          {isDoc && b.specialization && (
            <div style={{ fontSize: 13, color: 'var(--secondary)', marginBottom: 8 }}>{b.specialization}</div>
          )}
          <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
            <MdLocalHospital size={14} /> {displayHospitalName(b)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div className="detail-chip">
              <MdCalendarToday size={14} />
              <div>
                <div className="detail-chip-label">Date</div>
                <div className="detail-chip-value">{fmtDate(b.date)}</div>
              </div>
            </div>
            <div className="detail-chip">
              <MdAccessTime size={14} />
              <div>
                <div className="detail-chip-label">Time</div>
                <div className="detail-chip-value">
                  {forceEmergency && (b.slot === 'EMERGENCY' || b.slot === 'SOS') ? 'Immediate' : fmtTime(b.slot)}
                </div>
              </div>
            </div>
          </div>

          {b.patientName && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <MdPerson size={14} /> Patient: <strong style={{ color: 'var(--text)' }}>{b.patientName}</strong>
            </div>
          )}
          {b.consultationFee > 0 && (
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              Consultation fee: <strong>₹{b.consultationFee}</strong>
            </div>
          )}

          {/* Booking reference */}
          <div className="booking-ref-box" onClick={() => copyText(id, showToast, 'Booking ID copied!')}>
            <div className="booking-ref-label">BOOKING REFERENCE</div>
            <div className="booking-ref-id">{id}</div>
            <div className="booking-ref-hint"><MdContentCopy size={12} /> Tap to copy</div>
          </div>

          {/* OTP — matches Flutter Appointment Details */}
          {showOtp && (
            <CheckInOtp
              otp={b.otp || b.bookingOtp || b.checkInOtp}
              variant="box"
              onCopy={(code) => copyText(code, showToast, 'OTP copied!')}
            />
          )}

          {b.tokenNumber && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              Token: <span className="badge badge-blue">#{b.tokenNumber}</span>
            </div>
          )}

          {b.isRescheduled && !isCancelled && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--warning)', padding: '8px 12px', background: 'rgba(245,158,11,0.08)', borderRadius: 10 }}>
              Rescheduled{b.originalSlot ? ` from ${fmtTime(b.originalSlot)}` : ''}
            </div>
          )}

          {isCheckedIn && (
            <div style={{ marginTop: 12, padding: 12, background: 'rgba(0,184,148,0.08)', borderRadius: 12, fontSize: 13, color: '#00b894' }}>
              ✅ You have completed this appointment. Thank you for visiting!
            </div>
          )}
        </div>

        {forceEmergency && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5, padding: '0 4px' }}>
            ⚠️ Please proceed to the hospital immediately. Show this booking ID at reception.
          </div>
        )}

        {isSos && b.alertId && (
          <button type="button" className="btn btn-danger btn-full" onClick={openSosStatus} style={{ marginBottom: 10 }}>
            View Live SOS Status
          </button>
        )}

        {canModify && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }}
              onClick={openReschedule}>
              <MdRefresh size={16} /> Reschedule
            </button>
            <button type="button" className="btn btn-secondary" style={{ flex: 1, color: 'var(--danger)' }}
              onClick={cancelBooking} disabled={cancelling}>
              <MdCancel size={16} /> {cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        )}
      </div>

      {rescheduleOpen && (
        <div className="modal-overlay" onClick={() => setRescheduleOpen(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ fontWeight: 800 }}>Reschedule</div>
              <button type="button" onClick={() => setRescheduleOpen(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              {loadingSlots ? (
                <div className="spinner" style={{ margin: '20px auto' }} />
              ) : availableSlots.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--danger)', padding: 16 }}>No slots available</div>
              ) : (
                <div className="slot-grid">
                  {availableSlots.map(slot => (
                    <button key={slot} type="button"
                      className={`slot-btn${selectedSlot === slot ? ' selected' : ''}`}
                      onClick={() => setSelectedSlot(slot)}>
                      {fmtTime(slot)}
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="btn btn-primary btn-full"
                disabled={!selectedSlot || rescheduling} onClick={doReschedule}
                style={{ marginTop: 12 }}>
                {rescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
